import React, { useEffect, useRef, useState } from 'react';
import './ProcessStepsEditor.css';
import api from '../wrapper/API';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import Textfield from '../input/Textfield';
import Tooltip from '../tooltip/Tooltip';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import { useSuccessBar } from '../context/SuccessBarContext';
import { compareParticipantGroups, isPersonalLinkGroup } from '../../util/participantGroups';
import ProcessNotice from './ProcessNotice';
import {
  apiErrorMessage, isFinalStep, NEWSLETTER_PSEUDO_GROUP, normalizeStepKind, normalizeTargetKind, releaseLabel,
  STEP_KIND, STEP_KIND_DEFAULT_LABEL, STEP_KIND_ICON, STEP_KIND_LABEL, STEP_STATUS_LABEL, TARGET_KIND,
} from './processUtils';

const KIND_OPTIONS = [STEP_KIND.AssignLeaders, STEP_KIND.Release, STEP_KIND.SendMail, STEP_KIND.ManualCheck];

let clientKeyCounter = 0;
const nextClientKey = () => `new-${Date.now()}-${clientKeyCounter++}`;

// ProcessStepDto → editable row.
function toRow(step) {
  return {
    clientKey: `step-${step.stepId}`,
    stepId: step.stepId,
    kind: normalizeStepKind(step.kind) ?? STEP_KIND.ManualCheck,
    label: step.label ?? '',
    icon: step.icon ?? null,
    status: step.status,
    targets: (step.targets ?? []).map(t => ({
      targetKind: normalizeTargetKind(t.targetKind),
      participantGroupId: t.participantGroupId != null ? Number(t.participantGroupId) : null,
      textblockId: t.textblockId ?? null,
    })),
  };
}

const isNewsletterTarget = (t) => t.targetKind === TARGET_KIND.Newsletter;

/**
 * "Schritte anpassen": edit the step list of ONE process (copied from its template) — add, remove,
 * reorder, change kind/label/targets of pending and skipped steps; done steps are locked (they may
 * only move). → PUT /EventProcess/{id}/Steps. Shown inline instead of the stepper.
 */
function ProcessStepsEditor({ process, groups, groupsById, textblocks, ensureTextblocks, onSaved, onCancel }) {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [rows, setRows] = useState(() => (process.steps ?? []).filter(s => !isFinalStep(s)).map(toRow));
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const topRef = useRef(null);

  useEffect(() => { ensureTextblocks(); }, [ensureTextblocks]);

  // Allowed targets (same rules as the backend validator): releases only for personal-link groups and
  // the guest group (never for never-public processes); mails to every group except guests, whom the
  // newsletter reaches.
  const releaseGroups = groups.filter(g => (g.isGuest ? !process.neverPublic : isPersonalLinkGroup(g)));
  const mailGroups = groups.filter(g => !g.isGuest);
  const newsletterAllowed = !process.neverPublic;

  const rowErrors = (row) => {
    const list = [];
    const label = row.label.trim();
    if (!label) list.push('Bitte eine Bezeichnung angeben.');
    else if (label.length > 100) list.push('Die Bezeichnung darf höchstens 100 Zeichen lang sein.');
    if (row.kind === STEP_KIND.Release && row.targets.filter(t => !isNewsletterTarget(t)).length === 0) {
      list.push('Bitte mindestens eine Gruppe freigeben.');
    }
    if (row.kind === STEP_KIND.SendMail && row.targets.length === 0) list.push('Bitte mindestens ein Ziel wählen.');
    // Group targets may fall back to the group's mail rule, the newsletter has none (server validator).
    if (row.kind === STEP_KIND.SendMail && row.targets.some(t => isNewsletterTarget(t) && t.textblockId == null)) {
      list.push('Bitte für den Newsletter einen Textbaustein wählen.');
    }
    return list;
  };

  const warnings = [];
  if (process.requiresLeader && !rows.some(r => r.kind === STEP_KIND.AssignLeaders)) {
    warnings.push('Die Vorlage verlangt eine Spieltagleitung, der Prozess hat aber keinen Schritt der Art „Spieltagleiter".');
  }
  if (!rows.some(r => r.kind === STEP_KIND.Release)) {
    warnings.push('Ohne Freigabe-Schritt werden die Events nur über das Bearbeiten-Popup sichtbar.');
  }

  const updateRow = (clientKey, updater) => {
    setRows(prev => prev.map(r => (r.clientKey === clientKey ? updater(r) : r)));
  };

  const changeKind = (row, kind) => {
    updateRow(row.clientKey, r => {
      const label = !r.label.trim() || r.label === STEP_KIND_DEFAULT_LABEL[r.kind] ? STEP_KIND_DEFAULT_LABEL[kind] : r.label;
      const icon = !r.icon || r.icon === STEP_KIND_ICON[r.kind] ? STEP_KIND_ICON[kind] : r.icon;
      // Keep group targets that are valid for the new kind.
      let targets = [];
      if (kind === STEP_KIND.Release) {
        targets = r.targets.filter(t => !isNewsletterTarget(t) && releaseGroups.some(g => g.id === t.participantGroupId))
          .map(t => ({ ...t, textblockId: null }));
      } else if (kind === STEP_KIND.SendMail) {
        targets = r.targets.filter(t => !isNewsletterTarget(t) && mailGroups.some(g => g.id === t.participantGroupId));
      }
      return { ...r, kind, label, icon, targets };
    });
  };

  const toggleGroupTarget = (row, groupId) => {
    updateRow(row.clientKey, r => {
      const has = r.targets.some(t => !isNewsletterTarget(t) && t.participantGroupId === groupId);
      const targets = has
        ? r.targets.filter(t => isNewsletterTarget(t) || t.participantGroupId !== groupId)
        : [...r.targets, { targetKind: TARGET_KIND.ParticipantGroup, participantGroupId: groupId, textblockId: null }];
      return { ...r, targets };
    });
  };

  const toggleNewsletter = (row) => {
    updateRow(row.clientKey, r => {
      const has = r.targets.some(isNewsletterTarget);
      const targets = has
        ? r.targets.filter(t => !isNewsletterTarget(t))
        : [...r.targets, { targetKind: TARGET_KIND.Newsletter, participantGroupId: null, textblockId: null }];
      return { ...r, targets };
    });
  };

  const setTargetTextblock = (row, target, textblockId) => {
    updateRow(row.clientKey, r => ({
      ...r,
      targets: r.targets.map(t => (
        t.targetKind === target.targetKind && t.participantGroupId === target.participantGroupId
          ? { ...t, textblockId }
          : t
      )),
    }));
  };

  const move = (index, delta) => {
    setRows(prev => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeRow = (clientKey) => setRows(prev => prev.filter(r => r.clientKey !== clientKey));

  const addRow = () => {
    const kind = STEP_KIND.ManualCheck;
    setRows(prev => [...prev, {
      clientKey: nextClientKey(),
      stepId: null,
      kind,
      label: STEP_KIND_DEFAULT_LABEL[kind],
      icon: STEP_KIND_ICON[kind],
      status: 'pending',
      targets: [],
    }]);
  };

  const handleSave = () => {
    if (saving) return;
    setShowErrors(true);
    const editable = rows.filter(r => r.status !== 'done');
    if (editable.some(r => rowErrors(r).length > 0)) {
      setError('Bitte die markierten Schritte vervollständigen.');
      topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setError('');
    startSuccessBarLoading('Schritte werden gespeichert');
    api.put(`/EventProcess/${process.id}/Steps`, {
      steps: rows.map(r => ({
        stepId: r.stepId,
        kind: r.kind,
        label: r.label.trim(),
        icon: r.icon || null,
        targets: r.targets.map(t => ({
          targetKind: t.targetKind,
          participantGroupId: isNewsletterTarget(t) ? null : t.participantGroupId,
          textblockId: r.kind === STEP_KIND.SendMail ? (t.textblockId ?? null) : null,
        })),
      })),
    })
      .then(response => {
        startSuccessBarSuccess('Schritte gespeichert');
        onSaved(response.data);
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, 'Schritte konnten nicht gespeichert werden.'));
        topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      })
      .finally(() => setSaving(false));
  };

  // Groups shown as target chips: the allowed ones plus currently selected ones that are no longer
  // allowed (archived, changed channel) — so they can be removed.
  const chipGroups = (allowed, row) => {
    const ids = new Set(allowed.map(g => g.id));
    const extra = row.targets
      .filter(t => !isNewsletterTarget(t) && t.participantGroupId != null && !ids.has(t.participantGroupId))
      .map(t => groupsById.get(t.participantGroupId))
      .filter(Boolean);
    return [...allowed, ...extra].sort(compareParticipantGroups);
  };

  const targetName = (target) => (isNewsletterTarget(target)
    ? 'Newsletter'
    : (groupsById.get(target.participantGroupId)?.pluralName ?? 'Gruppe'));

  const renderTargets = (row, locked) => {
    if (row.kind === STEP_KIND.AssignLeaders || row.kind === STEP_KIND.ManualCheck) {
      return <p className={'processStepsEditorNoTargets'}>Dieser Schritt hat keine Ziele.</p>;
    }
    const isRelease = row.kind === STEP_KIND.Release;
    const allowed = isRelease ? releaseGroups : mailGroups;
    const selectedIds = new Set(row.targets.filter(t => !isNewsletterTarget(t)).map(t => t.participantGroupId));
    const newsletterSelected = row.targets.some(isNewsletterTarget);
    const groupsToShow = locked
      ? row.targets.filter(t => !isNewsletterTarget(t)).map(t => groupsById.get(t.participantGroupId)).filter(Boolean)
      : chipGroups(allowed, row);

    return (
      <div className={'processStepsEditorTargets'}>
        <span className={'processStepsEditorFieldLabel'}>{isRelease ? 'Freigeben für' : 'Mail an'}</span>
        <div className={'processStepsEditorChips'}>
          {groupsToShow.map(g => (
            <ParticipantGroupChip
              key={g.id}
              group={g}
              label={isRelease ? (g.isGuest ? 'Öffentlich (Gäste)' : releaseLabel(g)) : g.pluralName}
              active={selectedIds.has(g.id)}
              onClick={locked ? undefined : () => toggleGroupTarget(row, g.id)}
            />
          ))}
          {!isRelease && (newsletterAllowed || newsletterSelected) && (
            locked
              ? (newsletterSelected && <ParticipantGroupChip group={NEWSLETTER_PSEUDO_GROUP} />)
              : (
                <ParticipantGroupChip
                  group={NEWSLETTER_PSEUDO_GROUP}
                  active={newsletterSelected}
                  onClick={() => toggleNewsletter(row)}
                />
              )
          )}
        </div>
        {isRelease && !locked && (
          <p className={'processStepsEditorHint'}>
            {process.neverPublic
              ? 'Events dieses Prozesses sind nie öffentlich – Freigaben nur für Gruppen mit persönlichem Link.'
              : '„Öffentlich" gibt die Events für Gäste frei (Homepage); Gruppen mit Formular-Anmeldung sehen, was Gäste sehen.'}
          </p>
        )}
        {!isRelease && !locked && row.targets.length > 0 && (
          <div className={'processStepsEditorTextblocks'}>
            {row.targets.map(t => (
              <div key={`${t.targetKind}:${t.participantGroupId ?? 'n'}`} className={'processStepsEditorTextblock'}>
                <span className={'processStepsEditorTextblockLabel'}>Textbaustein für {targetName(t)}</span>
                <CustomDropdown
                  icon={'description'}
                  value={t.textblockId != null ? String(t.textblockId) : ''}
                  onChange={e => setTargetTextblock(row, t, e.target.value ? Number(e.target.value) : null)}
                >
                  <option value={''} disabled={isNewsletterTarget(t)}>
                    {isNewsletterTarget(t) ? 'Bitte Textbaustein wählen …' : 'Aus der Mail-Regel „Ankündigung“ der Gruppe'}
                  </option>
                  {(textblocks ?? []).map(tb => (
                    <option key={tb.id} value={String(tb.id)}>{tb.frontendName || tb.name}</option>
                  ))}
                </CustomDropdown>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={'processStepsEditor'} ref={topRef}>
      <div className={'processStepsEditorHeader'}>
        <h3>Schritte anpassen</h3>
        <p className={'processActionDesc'}>
          Gilt nur für diesen Prozess (die Vorlage bleibt unverändert). Erledigte Schritte sind gesperrt und
          lassen sich nur verschieben.
        </p>
      </div>

      {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}
      {warnings.map((w, i) => <ProcessNotice key={i} tone={'warning'}>{w}</ProcessNotice>)}

      <ol className={'processStepsEditorList'}>
        {rows.map((row, index) => {
          const locked = row.status === 'done';
          const errors = showErrors && !locked ? rowErrors(row) : [];
          return (
            <li key={row.clientKey} className={`processStepsEditorRow${locked ? ' isLocked' : ''}${errors.length ? ' hasError' : ''}`}>
              <div className={'processStepsEditorRowHead'}>
                <span className={'material-symbols-rounded processStepsEditorIcon'} aria-hidden={'true'}>{row.icon || STEP_KIND_ICON[row.kind]}</span>
                <span className={'processStepsEditorIndex'}>Schritt {index + 1}</span>
                <span className={`processStepsEditorStatus is-${row.stepId == null ? 'new' : row.status}`}>
                  {row.stepId == null ? 'Neu' : STEP_STATUS_LABEL[row.status] ?? row.status}
                </span>
                {locked && (
                  <span className={'processStepsEditorLock'}>
                    <span className={'material-symbols-rounded'} aria-hidden={'true'}>lock</span>
                    nicht änderbar
                  </span>
                )}
                <span className={'processStepsEditorRowActions'}>
                  <Tooltip text={'Nach oben'}>
                    <button type={'button'} className={'processStepsEditorBtn'} onClick={() => move(index, -1)} disabled={index === 0} aria-label={'Nach oben'}>
                      <span className={'material-symbols-rounded'} aria-hidden={'true'}>arrow_upward</span>
                    </button>
                  </Tooltip>
                  <Tooltip text={'Nach unten'}>
                    <button type={'button'} className={'processStepsEditorBtn'} onClick={() => move(index, 1)} disabled={index === rows.length - 1} aria-label={'Nach unten'}>
                      <span className={'material-symbols-rounded'} aria-hidden={'true'}>arrow_downward</span>
                    </button>
                  </Tooltip>
                  <Tooltip text={locked ? 'Erledigte Schritte können nicht entfernt werden' : 'Schritt entfernen'}>
                    <button type={'button'} className={'processStepsEditorBtn isDanger'} onClick={() => removeRow(row.clientKey)} disabled={locked} aria-label={'Schritt entfernen'}>
                      <span className={'material-symbols-rounded'} aria-hidden={'true'}>delete</span>
                    </button>
                  </Tooltip>
                </span>
              </div>

              {locked ? (
                <div className={'processStepsEditorLocked'}>
                  <span className={'processStepsEditorLockedLabel'}>{row.label}</span>
                  <span className={'processStepsEditorLockedKind'}>{STEP_KIND_LABEL[row.kind]}</span>
                </div>
              ) : (
                <div className={'processStepsEditorFields'}>
                  <div className={'processStepsEditorField'}>
                    <span className={'processStepsEditorFieldLabel'}>Art</span>
                    <CustomDropdown icon={'category'} value={row.kind} onChange={e => changeKind(row, e.target.value)}>
                      {KIND_OPTIONS.map(k => <option key={k} value={k}>{STEP_KIND_LABEL[k]}</option>)}
                    </CustomDropdown>
                  </div>
                  <div className={'processStepsEditorField'}>
                    <span className={'processStepsEditorFieldLabel'}>Bezeichnung (im Stepper)</span>
                    <Textfield
                      icon={'label'}
                      type={'text'}
                      value={row.label}
                      maxLength={100}
                      placeholder={STEP_KIND_DEFAULT_LABEL[row.kind]}
                      onChange={e => { const value = e.target.value; updateRow(row.clientKey, r => ({ ...r, label: value })); }}
                    />
                  </div>
                </div>
              )}

              {renderTargets(row, locked)}

              {errors.length > 0 && (
                <ul className={'processStepsEditorErrors'}>
                  {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </li>
          );
        })}
      </ol>

      <div className={'processStepsEditorFooter'}>
        <ActionButton icon={'add'} label={'Schritt hinzufügen'} type={3} onClick={addRow} disabled={saving} />
        <span className={'processStepsEditorFooterSpacer'} />
        <ActionButton icon={'close'} label={'Abbrechen'} onClick={onCancel} disabled={saving} />
        <ActionButton icon={'save'} label={'Speichern'} type={2} onClick={handleSave} disabled={saving} />
      </div>
    </div>
  );
}

export default ProcessStepsEditor;
